import { cn } from '@/lib/utils';
import { formatDuration } from '@/lib/format';

export interface Span {
  id: string;
  name: string;
  /** A short kind label: run, tool, step, error… */
  kind: string;
  depth: number;
  startMs: number;
  endMs: number | null;
  status: 'ok' | 'error' | 'running' | 'skipped';
  attributes: Record<string, unknown>;
}

const BAR: Record<Span['status'], string> = {
  ok: 'bg-foreground/70',
  error: 'bg-destructive',
  running: 'bg-foreground/40 animate-pulse',
  skipped: 'bg-muted-foreground/30',
};

/** Successful spans take the event timeline's colour for their kind. */
const KIND_BAR: Record<string, string> = {
  run: 'bg-tint-blue',
  workflow: 'bg-tint-blue',
  agent: 'bg-tint-violet',
  tool: 'bg-tint-amber',
  'model call': 'bg-tint-violet',
};

const barClass = (span: Span) =>
  span.status === 'ok' && KIND_BAR[span.kind] ? KIND_BAR[span.kind] : BAR[span.status];

/** Spans as a waterfall: one row per span, bars positioned on the trace's time axis. */
export function TraceWaterfall({
  spans,
  totalMs,
  selected,
  onSelect,
  compact = false,
}: {
  spans: Span[];
  totalMs: number;
  selected: string | null;
  onSelect: (id: string) => void;
  /** Narrow layout for side panels: a shorter name column and only the end ticks. */
  compact?: boolean;
}) {
  const total = Math.max(totalMs, 1);
  const ticks = compact ? [0, 1] : [0, 0.5, 1];
  const cols = compact
    ? 'grid-cols-[minmax(5rem,9rem)_1fr_3.5rem]'
    : 'grid-cols-[minmax(8rem,14rem)_1fr_4.5rem]';
  return (
    <div className="grid gap-1 text-xs" role="table" aria-label="Trace spans">
      <div className={cn('text-muted-foreground grid items-end gap-3 pb-1', cols)} role="row">
        <span role="columnheader">Span</span>
        <span className="relative h-4" role="columnheader" aria-label="Timeline">
          {ticks.map((t) => (
            <span
              key={t}
              className="absolute -translate-x-1/2 tabular-nums whitespace-nowrap first:translate-x-0 last:-translate-x-full"
              style={{ left: `${t * 100}%` }}
            >
              {formatDuration(t * total)}
            </span>
          ))}
        </span>
        <span className="text-right" role="columnheader">
          Duration
        </span>
      </div>
      {spans.map((span) => {
        const end = span.endMs ?? total;
        const left = (span.startMs / total) * 100;
        const width = Math.max(((end - span.startMs) / total) * 100, 0.6);
        const duration = span.endMs === null ? null : span.endMs - span.startMs;
        return (
          <button
            type="button"
            role="row"
            key={span.id}
            onClick={() => onSelect(span.id)}
            aria-pressed={selected === span.id}
            className={cn(
              'hover:bg-muted grid items-center gap-3 rounded px-1 py-1 text-left transition-colors',
              cols,
              selected === span.id && 'bg-muted',
            )}
            data-testid="trace-span"
          >
            <span
              className="flex min-w-0 items-center gap-1.5"
              style={{ paddingLeft: span.depth * (compact ? 10 : 14) }}
              title={`${span.kind}: ${span.name}`}
              role="cell"
            >
              {!compact && (
                <span className="text-muted-foreground shrink-0 rounded border px-1 text-[10px] uppercase">
                  {span.kind}
                </span>
              )}
              <span className="truncate font-mono">{span.name}</span>
            </span>
            <span className="bg-muted/50 relative h-3 rounded-sm" role="cell">
              {[0.25, 0.5, 0.75].map((t) => (
                <span
                  key={t}
                  className="bg-border absolute inset-y-0 w-px"
                  style={{ left: `${t * 100}%` }}
                  aria-hidden
                />
              ))}
              <span
                className={cn('absolute inset-y-0 rounded-sm', barClass(span))}
                style={{ left: `${left}%`, width: `${Math.min(width, 100 - left)}%` }}
                title={`${span.name}: ${duration === null ? 'running' : formatDuration(duration)}`}
              />
            </span>
            <span className="text-muted-foreground text-right tabular-nums" role="cell">
              {span.status === 'skipped' ? 'skipped' : formatDuration(duration)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
